import {
  COST_BANDS,
  FOOD_CUISINES,
  FOOD_DIET_CLASSES,
  FOOD_MEAL_TIMES,
  HEALTH_TIERS,
  MEAL_ROLES,
  PORTION_UNITS,
  PREP_TIME_BANDS,
  type AdminFoodDetail,
  type CostBandName,
  type FoodDietClassName,
  type HealthTierName,
  type MealRoleName,
  type PortionUnitName,
} from '@steado/admin-contract/foods';
import { ALLERGENS, type AllergenName } from '@steado/admin-contract/user-detail';
import { CheckIcon, PlusIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { Panel } from '@/components/panel/panels';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { humanise } from '@/lib/format';
import { cn } from '@/lib/utils';
import { sourceTitle } from '@/screens/catalog-history/event-copy';
import { BeforeAfter, Figures } from '@/screens/catalog-history/history-parts';
import { JAIN_AVOIDS_HINT } from '@/screens/foods/JainAnswer';

import {
  NUTRITION_FIELDS,
  checkNutrition,
  checkPortions,
  draftOf,
  parseFigure,
  type DiffLine,
  type EditDraft,
  type PortionDraft,
} from './food-edit-model';

/**
 * The food editor's panels: per 100 g, portions, names, allergens, the
 * curated facts, and the note and sources the save carries into History.
 * Each reads the draft and reports a patch; `FoodDetailScreen` holds the
 * draft and the one save.
 */

export interface PanelProps {
  readonly food: AdminFoodDetail;
  readonly draft: EditDraft;
  readonly disabled: boolean;
  /** Field errors from the server's last refusal, by request path. */
  readonly serverErrors: Readonly<Record<string, string>>;
  readonly onChange: (patch: Partial<EditDraft>) => void;
}

const HEAD =
  'border-t border-border bg-secondary px-[18px] py-2 text-[11px] font-semibold tracking-[0.055em] text-muted-foreground uppercase';

function one(n: number): string {
  return (Math.round(n * 10) / 10).toFixed(1);
}

// --- per 100 g ------------------------------------------------------------------

export function NutritionPanel({ food, draft, disabled, serverErrors, onChange }: PanelProps) {
  const check = checkNutrition(food, draft.nutrition);
  const was = draftOf(food).nutrition;
  const n = (id: keyof EditDraft['nutrition']) => parseFigure(draft.nutrition[id]) ?? 0;
  const pct = Math.round(check.apart * 100);
  const tail = check.passes
    ? ' is within 1%.'
    : ` is ${String(pct)}% ${check.kcal < check.macroKcal ? 'below' : 'above'} that.${
        check.blocks
          ? ' More than 10% apart: change one to save.'
          : ' Check the energy or the macros; you can still save.'
      }`;

  return (
    <Panel title="Per 100 g" description="As eaten. Every food's numbers go through the same checks." bleed>
      <div className={cn('grid grid-cols-[minmax(110px,1fr)_132px_minmax(0,1fr)] gap-3', HEAD)}>
        <span>Field</span>
        <span>Value</span>
        <span>Allowed</span>
      </div>
      {NUTRITION_FIELDS.map((field) => {
        const state = check.fields.find((candidate) => candidate.id === field.id);
        const error = state?.error ?? null;
        const changed = state?.changed === true;
        const inputId = `nut-${field.id}`;
        return (
          <div
            key={field.id}
            className="grid grid-cols-[minmax(110px,1fr)_132px_minmax(0,1fr)] items-center gap-x-3 gap-y-1 border-t border-border px-[18px] py-2"
          >
            <Label htmlFor={inputId} className="text-[13px] font-normal text-muted-foreground">
              {field.label}
            </Label>
            <span className="relative flex items-center">
              <Input
                id={inputId}
                inputMode="decimal"
                value={draft.nutrition[field.id]}
                disabled={disabled}
                aria-invalid={error !== null}
                aria-describedby={`${inputId}-hint`}
                placeholder={field.id === 'fibre' ? 'not stated' : undefined}
                onChange={(event) => {
                  onChange({ nutrition: { ...draft.nutrition, [field.id]: event.target.value } });
                }}
                className={cn(
                  'h-8 pr-10 font-mono text-[13px] font-medium tabular-nums',
                  changed && error === null && 'border-warn-fg/60',
                )}
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute right-3 text-[12px] text-muted-foreground"
              >
                {field.unit}
              </span>
            </span>
            <span
              id={`${inputId}-hint`}
              className="flex flex-wrap gap-x-2.5 gap-y-0.5 text-[12px] text-muted-foreground"
            >
              <Figures text={`0 to ${String(field.max)}`} />
              {changed ? (
                <span className="text-warn-fg">
                  was{' '}
                  <Figures text={was[field.id] === '' ? 'not stated' : `${was[field.id]} ${field.unit}`} />
                </span>
              ) : null}
              {error === null ? null : <span className="font-medium text-err-fg">{error}</span>}
            </span>
          </div>
        );
      })}
      <div className="flex items-start gap-2 border-t border-border bg-secondary/40 px-[18px] pt-2.5 pb-3">
        <span
          role="img"
          aria-label={check.passes ? 'Passes' : 'Does not match'}
          className={cn(
            'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
            check.passes ? 'bg-ok-bg text-ok-fg' : 'bg-warn-bg text-warn-fg',
          )}
        >
          {check.passes ? <CheckIcon aria-hidden="true" className="size-3" /> : '!'}
        </span>
        <span role="status" className="flex min-w-0 flex-col gap-0.5 text-[13px] text-foreground">
          <span className="text-[12px] font-medium text-muted-foreground">Macro check, live</span>
          <span className="text-pretty">
            <Figures
              text={`Protein, carbohydrate and fat add up to ${one(check.macroKcal)} kcal. Energy ${String(check.kcal)}${tail}`}
            />
          </span>
          <span className="font-mono text-[12.5px] text-muted-foreground tabular-nums">
            {one(n('protein'))} × 4 + {one(n('carbs'))} × 4 + {one(n('fat'))} × 9 = {one(check.macroKcal)}
          </span>
          <span className="text-[12px] text-muted-foreground">
            Protein, carbohydrate, fat and fibre: <Figures text={`${one(check.grams)} g of 100 g.`} />
          </span>
          {serverErrors.nutrition === undefined ? null : (
            <span role="alert" className="text-[12.5px] font-medium text-err-fg">
              {serverErrors.nutrition}
            </span>
          )}
        </span>
      </div>
    </Panel>
  );
}

// --- portions --------------------------------------------------------------------

let portionKeys = 0;

export function PortionsPanel({ food, draft, disabled, serverErrors, onChange }: PanelProps) {
  const checks = checkPortions(draft.portions);
  const setPortion = (key: string, patch: Partial<PortionDraft>) => {
    onChange({
      portions: draft.portions.map((portion) => (portion.key === key ? { ...portion, ...patch } : portion)),
    });
  };
  const grid = 'grid grid-cols-[minmax(0,1.4fr)_minmax(96px,0.8fr)_96px_104px_32px] gap-2.5';

  return (
    <Panel title="Portions" description="The default is what one tap logs. Grams from 1 to 2,000." bleed>
      <div className="overflow-x-auto">
        <div className="min-w-[520px]">
          <div className={cn(grid, HEAD)}>
            <span>Name</span>
            <span>Unit</span>
            <span>Grams</span>
            <span>Default</span>
            <span />
          </div>
          <div role="radiogroup" aria-label="Default portion">
            {draft.portions.map((portion, index) => {
              const was = food.portions.find((candidate) => candidate.id === portion.id);
              const check = checks[index];
              const serverError =
                serverErrors[`portions.${String(index)}.grams`] ??
                serverErrors[`portions.${String(index)}.label`] ??
                serverErrors[`portions.${String(index)}`];
              const error = check?.error ?? serverError ?? null;
              const grams = parseFigure(portion.grams);
              const note =
                error ??
                (was === undefined
                  ? 'New portion'
                  : grams !== was.grams
                    ? `Was ${String(was.grams)} g`
                    : null);
              const name = portion.label.trim() === '' ? 'this portion' : portion.label.trim();
              return (
                <div
                  key={portion.key}
                  className={cn(
                    grid,
                    'items-center gap-y-1 border-t border-border px-[18px] py-2',
                    was === undefined && 'bg-ok-bg/40',
                  )}
                >
                  <span className="relative flex items-center">
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute left-3 font-mono text-[13px] text-muted-foreground"
                    >
                      1
                    </span>
                    <Input
                      aria-label="Portion name"
                      value={portion.label}
                      disabled={disabled}
                      aria-invalid={check?.error !== null && check?.gramsError !== true}
                      onChange={(event) => {
                        setPortion(portion.key, { label: event.target.value });
                      }}
                      className="h-8 pl-6 text-[13px]"
                    />
                  </span>
                  <Select
                    value={portion.unit}
                    disabled={disabled}
                    onValueChange={(unit) => {
                      setPortion(portion.key, { unit: unit as PortionUnitName });
                    }}
                  >
                    <SelectTrigger
                      aria-label={`Unit for ${name}`}
                      size="sm"
                      className="h-8 w-full text-[13px]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PORTION_UNITS.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {humanise(unit)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="relative flex items-center">
                    <Input
                      aria-label={`Grams for ${name}`}
                      inputMode="decimal"
                      value={portion.grams}
                      disabled={disabled}
                      aria-invalid={check?.gramsError === true || serverError !== undefined}
                      onChange={(event) => {
                        setPortion(portion.key, { grams: event.target.value });
                      }}
                      className="h-8 pr-7 font-mono text-[13px] font-medium tabular-nums"
                    />
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute right-3 text-[12px] text-muted-foreground"
                    >
                      g
                    </span>
                  </span>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={portion.isDefault}
                    aria-label={portion.isDefault ? `Default portion: ${name}` : `Make ${name} the default`}
                    disabled={disabled}
                    onClick={() => {
                      onChange({
                        portions: draft.portions.map((candidate) => ({
                          ...candidate,
                          isDefault: candidate.key === portion.key,
                        })),
                      });
                    }}
                    className="inline-flex h-8 items-center gap-2 rounded-md px-1 text-[13px] font-medium text-foreground disabled:opacity-50"
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px]',
                        portion.isDefault ? 'border-foreground' : 'border-muted-foreground',
                      )}
                    >
                      <span
                        className={cn(
                          'size-2 rounded-full',
                          portion.isDefault ? 'bg-foreground' : 'bg-transparent',
                        )}
                      />
                    </span>
                    {portion.isDefault ? 'Default' : ''}
                  </button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove ${name}`}
                    disabled={disabled}
                    onClick={() => {
                      const rest = draft.portions.filter((candidate) => candidate.key !== portion.key);
                      const keepDefault = rest.some((candidate) => candidate.isDefault);
                      onChange({
                        portions: rest.map((candidate, at) => ({
                          ...candidate,
                          isDefault: keepDefault ? candidate.isDefault : at === 0,
                        })),
                      });
                    }}
                  >
                    <Trash2Icon aria-hidden="true" className="text-muted-foreground" />
                  </Button>
                  {note === null ? null : (
                    <span
                      className={cn(
                        'col-span-full text-[12px]',
                        error !== null ? 'text-err-fg' : was === undefined ? 'text-ok-fg' : 'text-warn-fg',
                      )}
                    >
                      {note}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border px-[18px] pt-2 pb-2.5">
        <span className="min-w-[15rem] flex-1 text-[12px] text-muted-foreground">
          <Figures text="100 g is always offered and cannot be removed." />
          {serverErrors.portions === undefined ? null : (
            <span role="alert" className="block font-medium text-err-fg">
              {serverErrors.portions}
            </span>
          )}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || draft.portions.length >= 8}
          onClick={() => {
            portionKeys += 1;
            onChange({
              portions: [
                ...draft.portions,
                {
                  key: `new-${String(portionKeys)}`,
                  id: null,
                  label: '',
                  unit: 'piece',
                  grams: '',
                  isDefault: draft.portions.length === 0,
                },
              ],
            });
          }}
        >
          <PlusIcon aria-hidden="true" />
          Add a portion
        </Button>
      </div>
    </Panel>
  );
}

// --- names ---------------------------------------------------------------------------

export function NamesPanel({ food, draft, disabled, serverErrors, onChange }: PanelProps) {
  const [aliasDraft, setAliasDraft] = useState('');
  const [aliasError, setAliasError] = useState<string | null>(null);
  const labelId = useId();
  const empty = draft.displayName.trim() === '';
  const shownName = draft.displayName.trim() === '' ? food.name : draft.displayName.trim();

  const add = () => {
    const alias = aliasDraft.trim();
    if (alias === '') return;
    const taken = [draft.displayName, food.name, ...draft.aliases.map((candidate) => candidate.alias)].some(
      (name) => name.trim().toLowerCase() === alias.toLowerCase(),
    );
    if (taken) {
      setAliasError(`${shownName} already answers to ${alias}.`);
      return;
    }
    setAliasError(null);
    onChange({ aliases: [...draft.aliases, { id: null, alias, language: null }] });
    setAliasDraft('');
  };

  return (
    <Panel title="Names it answers to" bleed>
      <div className="flex flex-col gap-1.5 border-t border-border px-[18px] py-3">
        <Label htmlFor="display-name" className="text-[12px] font-medium text-muted-foreground">
          Display name
        </Label>
        <Input
          id="display-name"
          value={draft.displayName}
          disabled={disabled}
          aria-invalid={empty}
          aria-describedby="display-name-hint"
          onChange={(event) => {
            onChange({ displayName: event.target.value });
          }}
          className="h-8 text-[13px] font-medium"
        />
        <span
          id="display-name-hint"
          className={cn('text-[12px]', empty ? 'text-err-fg' : 'text-muted-foreground')}
        >
          {empty ? 'A food needs a display name.' : 'What the app shows. Search also finds every other name.'}
        </span>
      </div>
      <div className="flex flex-col gap-2 border-t border-border px-[18px] pt-3 pb-3.5">
        <span id={labelId} className="text-[12px] font-medium text-muted-foreground">
          Other names
        </span>
        {draft.aliases.length === 0 ? (
          <span className="text-[13px] text-muted-foreground">None yet. Only the display name finds it.</span>
        ) : (
          <ul aria-labelledby={labelId} className="flex flex-wrap gap-1.5">
            {draft.aliases.map((alias) => (
              <li
                key={alias.id ?? `new:${alias.alias}`}
                className={cn(
                  'inline-flex items-center rounded-lg border pl-2.5 text-[13px] text-foreground',
                  alias.id === null ? 'border-ok-fg/30 bg-ok-bg/50' : 'border-input/70 bg-background',
                )}
              >
                {alias.alias}
                {alias.language === null ? null : (
                  <span className="ml-1.5 font-mono text-[11.5px] text-muted-foreground">
                    {alias.language}
                  </span>
                )}
                {alias.id === null ? (
                  <span className="ml-1.5 text-[12px] font-medium text-ok-fg">new</span>
                ) : null}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove the name ${alias.alias}`}
                  disabled={disabled}
                  onClick={() => {
                    onChange({ aliases: draft.aliases.filter((candidate) => candidate !== alias) });
                  }}
                  className="hover:bg-transparent"
                >
                  <XIcon aria-hidden="true" className="size-3.5 text-muted-foreground" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <label htmlFor="alias-new" className="sr-only">
            Add another name
          </label>
          <Input
            id="alias-new"
            value={aliasDraft}
            disabled={disabled}
            placeholder="Add another name, such as a common spelling"
            aria-describedby="alias-hint"
            onChange={(event) => {
              setAliasDraft(event.target.value);
              setAliasError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                add();
              }
            }}
            className="h-8 min-w-0 flex-1 text-[13px]"
          />
          <Button variant="outline" size="sm" disabled={disabled || aliasDraft.trim() === ''} onClick={add}>
            Add name
          </Button>
        </div>
        <span id="alias-hint" className="text-[12px] text-muted-foreground">
          Spellings and other languages people type. Each one finds {shownName} in search and in logging.
        </span>
        {(aliasError ?? serverErrors['aliases.add'] ?? serverErrors['aliases.remove']) ===
        undefined ? null : (
          <span role="alert" className="text-[12.5px] font-medium text-err-fg">
            {aliasError ?? serverErrors['aliases.add'] ?? serverErrors['aliases.remove']}
          </span>
        )}
      </div>
    </Panel>
  );
}

// --- allergens -------------------------------------------------------------------------

const ALLERGEN_SOURCE_LABEL: Record<AdminFoodDetail['allergenSource'], string> = {
  unassessed: 'Nobody has checked yet, so it is withheld from users with allergies.',
  source_stated: 'Stated by the source or by someone in the console.',
  ingredient_derived: 'Worked out from its recipe.',
};

export function AllergensPanel({ food, draft, disabled, serverErrors, onChange }: PanelProps) {
  const shownName = draft.displayName.trim() === '' ? food.name : draft.displayName.trim();
  return (
    <Panel title="Allergens" bleed>
      <div className="flex flex-col gap-2.5 border-t border-border px-[18px] pt-3 pb-3.5">
        <div role="group" aria-label="Allergens" className="flex flex-wrap gap-1.5">
          {ALLERGENS.map((allergen) => {
            const on = draft.allergens.includes(allergen);
            return (
              <button
                key={allergen}
                type="button"
                aria-pressed={on}
                disabled={disabled}
                onClick={() => {
                  onChange({
                    allergens: on
                      ? draft.allergens.filter((value) => value !== allergen)
                      : ALLERGENS.filter(
                          (value: AllergenName) => value === allergen || draft.allergens.includes(value),
                        ),
                  });
                }}
                className={cn(
                  'inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 pl-2.5 text-[13px] capitalize transition-colors disabled:opacity-50',
                  on
                    ? 'border-foreground bg-foreground font-semibold text-background'
                    : 'border-input bg-card text-foreground hover:bg-accent',
                )}
              >
                {on ? (
                  <CheckIcon aria-hidden="true" className="size-3.5" />
                ) : (
                  <PlusIcon aria-hidden="true" className="size-3.5 text-muted-foreground" />
                )}
                {allergen}
              </button>
            );
          })}
        </div>
        <p className="max-w-[70ch] text-[13px] text-pretty">
          <span className="font-medium text-foreground">
            Not sure a common version has it? Add it (safer).
          </span>{' '}
          <span className="text-muted-foreground">
            {shownName} is then not offered to anyone who avoids it. Leaving it out offers it to them.
          </span>
        </p>
        <span className="text-[12px] text-muted-foreground">
          {ALLERGEN_SOURCE_LABEL[food.allergenSource]}
        </span>
        {(serverErrors.allergens ?? serverErrors.dietClass) === undefined ? null : (
          <span role="alert" className="text-[12.5px] font-medium text-err-fg">
            {serverErrors.allergens ?? serverErrors.dietClass}
          </span>
        )}
      </div>
    </Panel>
  );
}

// --- note and sources ------------------------------------------------------------------------

export function NoteSourcesPanel({ draft, disabled, onChange }: Omit<PanelProps, 'food' | 'serverErrors'>) {
  const [link, setLink] = useState('');
  const add = () => {
    const source = link.trim();
    if (source === '' || draft.sources.includes(source)) return;
    onChange({ sources: [...draft.sources, source] });
    setLink('');
  };
  return (
    <Panel
      title="Note and sources"
      description="Saved with this change in History, so the next person sees why."
      bleed
    >
      <div className="flex flex-col gap-1.5 border-t border-border px-[18px] py-3">
        <Label htmlFor="edit-note" className="text-[12px] font-medium text-muted-foreground">
          Note
        </Label>
        <Textarea
          id="edit-note"
          rows={2}
          value={draft.note}
          disabled={disabled}
          maxLength={2000}
          onChange={(event) => {
            onChange({ note: event.target.value });
          }}
          className="text-[13px]"
        />
      </div>
      {draft.sources.length === 0 ? null : (
        <ul className="flex flex-col">
          {draft.sources.map((source, index) => (
            <li
              key={source}
              className="grid grid-cols-[minmax(0,1fr)_32px] items-center gap-1 border-t border-border px-[18px] py-1"
            >
              <span className="flex min-w-0 items-baseline gap-2 text-[13px]">
                <span className="font-mono text-[12px] text-info-fg">[{index + 1}]</span>
                <span className="flex min-w-0 flex-col">
                  <span className="font-medium break-words text-foreground">{sourceTitle(source).title}</span>
                  {sourceTitle(source).href === null ? null : (
                    <span className="truncate text-[12px] text-muted-foreground">{source}</span>
                  )}
                </span>
              </span>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove source ${source}`}
                disabled={disabled}
                onClick={() => {
                  onChange({ sources: draft.sources.filter((candidate) => candidate !== source) });
                }}
              >
                <XIcon aria-hidden="true" className="size-3.5 text-muted-foreground" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2 border-t border-border px-[18px] pt-2.5 pb-3.5">
        <label htmlFor="source-new" className="sr-only">
          Add a source link
        </label>
        <Input
          id="source-new"
          type="url"
          value={link}
          disabled={disabled}
          placeholder="Paste a source link"
          onChange={(event) => {
            setLink(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add();
            }
          }}
          className="h-8 min-w-0 flex-1 text-[13px]"
        />
        <Button variant="outline" size="sm" disabled={disabled || link.trim() === ''} onClick={add}>
          Add source
        </Button>
      </div>
    </Panel>
  );
}

// --- the curated facts ----------------------------------------------------------------------

/** Radix has no empty option, so "nobody has decided yet" needs a value of its own. */
const UNCURATED = '__uncurated__';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function CurationRow({
  label,
  hint,
  children,
}: {
  readonly label: string;
  readonly hint: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 items-start gap-2 border-t border-border px-[18px] py-2.5 sm:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] sm:gap-3">
      <span className="flex flex-col gap-0.5">
        <span className="text-[13px] text-foreground">{label}</span>
        <span className="text-[12px] text-pretty text-muted-foreground">{hint}</span>
      </span>
      <span className="flex min-w-0 items-center">{children}</span>
    </div>
  );
}

function Toggle({
  pressed,
  disabled,
  onClick,
  children,
}: {
  readonly pressed: boolean;
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'h-[26px] rounded-md border px-2 text-[12.5px] transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        pressed
          ? 'border-primary bg-primary/10 font-medium text-primary'
          : 'border-input bg-card text-muted-foreground hover:bg-accent',
      )}
    >
      {children}
    </button>
  );
}

function BandSelect<T extends string>({
  label,
  value,
  values,
  disabled,
  onChange,
}: {
  readonly label: string;
  readonly value: T | null;
  readonly values: readonly T[];
  readonly disabled: boolean;
  readonly onChange: (value: T | null) => void;
}) {
  return (
    <Select
      value={value ?? UNCURATED}
      disabled={disabled}
      onValueChange={(next) => {
        onChange(next === UNCURATED ? null : (next as T));
      }}
    >
      <SelectTrigger aria-label={label} className="h-7 w-full text-[13px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={UNCURATED}>Uncurated</SelectItem>
        {values.map((option) => (
          <SelectItem key={option} value={option}>
            {humanise(option)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function YesNo({
  id,
  label,
  checked,
  disabled,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onChange: (checked: boolean) => void;
}) {
  return (
    <span className="flex h-7 items-center gap-2">
      <Checkbox
        id={id}
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => {
          onChange(next === true);
        }}
      />
      <Label htmlFor={id} className="text-[13px] font-normal text-muted-foreground">
        {checked ? 'Yes' : 'No'}
      </Label>
    </span>
  );
}

const DIET_CLASS_SOURCE_LABEL: Record<NonNullable<AdminFoodDetail['dietClassSource']>, string> = {
  source_stated: 'Stated by the source',
  name_inferred: 'Guessed from the dish’s name',
  ingredient_derived: 'Derived from its recipe',
  reviewed: 'Confirmed by a curator',
};

export function CurationPanel({ food, draft, disabled, serverErrors, onChange }: PanelProps) {
  const ids = {
    common: useId(),
    onion: useId(),
    jain: useId(),
    servable: useId(),
  };
  return (
    <Panel title="Curation" bleed>
      <CurationRow
        label="Everyday food in India"
        hint="Plans prefer it. Unticking demotes rather than bans, so a narrow diet can still be planned."
      >
        <YesNo
          id={ids.common}
          label="Everyday food in India"
          checked={draft.isCommonInIndia}
          disabled={disabled}
          onChange={(isCommonInIndia) => {
            onChange({ isCommonInIndia });
          }}
        />
      </CurationRow>
      <CurationRow
        label="Eaten as it is"
        hint="Unticked keeps it out of plans. Search and logging still find it."
      >
        <YesNo
          id={ids.servable}
          label="Eaten as it is"
          checked={draft.servableAsIs}
          disabled={disabled}
          onChange={(servableAsIs) => {
            onChange({ servableAsIs });
          }}
        />
      </CurationRow>
      <CurationRow
        label="Diet class"
        hint={
          food.dietClassSource === null
            ? 'Nobody has reviewed this. It is whatever the importer guessed.'
            : DIET_CLASS_SOURCE_LABEL[food.dietClassSource]
        }
      >
        <Select
          value={draft.dietClass}
          disabled={disabled}
          onValueChange={(next) => {
            onChange({ dietClass: next as FoodDietClassName });
          }}
        >
          <SelectTrigger aria-label="Diet class" className="h-7 w-full text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FOOD_DIET_CLASSES.map((value) => (
              <SelectItem key={value} value={value}>
                {humanise(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CurationRow>
      <CurationRow
        label="Contains onion or garlic"
        hint="Excluded for anyone avoiding both, whatever the diet class says."
      >
        <YesNo
          id={ids.onion}
          label="Contains onion or garlic"
          checked={draft.hasOnionGarlic}
          disabled={disabled}
          onChange={(hasOnionGarlic) => {
            onChange({ hasOnionGarlic });
          }}
        />
      </CurationRow>
      <CurationRow label="Jain avoids" hint={`${JAIN_AVOIDS_HINT} Excluded from both Jain diets.`}>
        <span className="flex flex-col gap-1">
          <YesNo
            id={ids.jain}
            label="Jain avoids"
            checked={draft.jainExcluded}
            disabled={disabled}
            onChange={(jainExcluded) => {
              onChange({ jainExcluded });
            }}
          />
          {serverErrors.jainExcluded === undefined ? null : (
            <span role="alert" className="text-[12.5px] font-medium text-err-fg">
              {serverErrors.jainExcluded}
            </span>
          )}
        </span>
      </CurationRow>
      <CurationRow
        label="Cuisines"
        hint="Up to four. “Universal” is for staples every regional table shares; untagged ranks the same."
      >
        <span className="flex flex-wrap gap-1.5 py-0.5">
          {FOOD_CUISINES.map((cuisine) => {
            const selected = draft.cuisines.includes(cuisine);
            return (
              <Toggle
                key={cuisine}
                pressed={selected}
                disabled={disabled || (!selected && draft.cuisines.length >= 4)}
                onClick={() => {
                  onChange({
                    cuisines: selected
                      ? draft.cuisines.filter((value) => value !== cuisine)
                      : [...draft.cuisines, cuisine],
                  });
                }}
              >
                {humanise(cuisine)}
              </Toggle>
            );
          })}
        </span>
      </CurationRow>
      <CurationRow label="Meal role" hint="What this anchors a meal with.">
        <BandSelect<MealRoleName>
          label="Meal role"
          value={draft.mealRole}
          values={MEAL_ROLES}
          disabled={disabled}
          onChange={(mealRole) => {
            onChange({ mealRole });
          }}
        />
      </CurationRow>
      <CurationRow label="Meal times" hint="When it is eaten. None picked fits every meal.">
        <span className="flex flex-col gap-1">
          <span className="flex flex-wrap gap-1.5 py-0.5">
            {FOOD_MEAL_TIMES.map((slot) => {
              const selected = draft.mealTimes.includes(slot);
              return (
                <Toggle
                  key={slot}
                  pressed={selected}
                  disabled={disabled}
                  onClick={() => {
                    onChange({
                      mealTimes: FOOD_MEAL_TIMES.filter((value) =>
                        value === slot ? !selected : draft.mealTimes.includes(value),
                      ),
                    });
                  }}
                >
                  {humanise(slot)}
                </Toggle>
              );
            })}
          </span>
          {serverErrors.mealTimes === undefined ? null : (
            <span role="alert" className="text-[12.5px] font-medium text-err-fg">
              {serverErrors.mealTimes}
            </span>
          )}
        </span>
      </CurationRow>
      <CurationRow label="Months in season" hint="None picked means all year.">
        <span className="flex flex-wrap gap-1.5 py-0.5">
          {MONTHS.map((month, index) => {
            const value = index + 1;
            const selected = draft.seasonMonths.includes(value);
            return (
              <Toggle
                key={month}
                pressed={selected}
                disabled={disabled}
                onClick={() => {
                  onChange({
                    seasonMonths: selected
                      ? draft.seasonMonths.filter((m) => m !== value)
                      : [...draft.seasonMonths, value].sort((a, b) => a - b),
                  });
                }}
              >
                {month}
              </Toggle>
            );
          })}
        </span>
      </CurationRow>
      <CurationRow label="Health tier" hint="Treats fill a plan only when everyday foods run short.">
        <BandSelect<HealthTierName>
          label="Health tier"
          value={draft.healthTier}
          values={HEALTH_TIERS}
          disabled={disabled}
          onChange={(healthTier) => {
            onChange({ healthTier });
          }}
        />
      </CurationRow>
      <CurationRow label="Prep time" hint="How long it takes to cook. Shown to the model, never a filter.">
        <BandSelect
          label="Prep time"
          value={draft.prepTimeBand}
          values={PREP_TIME_BANDS}
          disabled={disabled}
          onChange={(prepTimeBand) => {
            onChange({ prepTimeBand });
          }}
        />
      </CurationRow>
      <CurationRow label="Cost" hint="What it typically costs. Shown to the model, never a filter.">
        <BandSelect<CostBandName>
          label="Cost"
          value={draft.costBand}
          values={COST_BANDS}
          disabled={disabled}
          onChange={(costBand) => {
            onChange({ costBand });
          }}
        />
      </CurationRow>
    </Panel>
  );
}

// --- review and save --------------------------------------------------------------------------

export function ReviewBar({
  problems,
  changes,
  disabled,
  onReview,
}: {
  readonly problems: number;
  readonly changes: number;
  readonly disabled: boolean;
  readonly onReview: () => void;
}) {
  const titleId = useId();
  const blocked = problems > 0 || changes === 0;
  const title =
    problems > 0
      ? `Fix ${String(problems)} ${problems === 1 ? 'value' : 'values'} to save`
      : changes > 0
        ? `${String(changes)} ${changes === 1 ? 'value' : 'values'} changed, not saved`
        : 'No changes yet';
  return (
    <div className="sticky bottom-3 z-10 flex flex-wrap items-center gap-x-4 gap-y-2.5 rounded-xl border border-border bg-card px-[18px] py-3.5 shadow-lg">
      <span className="flex min-w-[18rem] flex-1 flex-col gap-0.5">
        <span
          id={titleId}
          className={cn(
            'text-[13px] font-medium',
            problems > 0 ? 'text-err-fg' : changes > 0 ? 'text-warn-fg' : 'text-muted-foreground',
          )}
        >
          {title}
        </span>
        <span className="text-[12px] text-muted-foreground">
          Nothing is saved until you confirm what changes.
        </span>
      </span>
      {blocked || disabled ? (
        <Button
          size="lg"
          variant="secondary"
          aria-disabled="true"
          aria-describedby={titleId}
          className="cursor-not-allowed text-muted-foreground hover:bg-secondary"
          onClick={(event) => {
            event.preventDefault();
          }}
        >
          Review and save
        </Button>
      ) : (
        <Button size="lg" onClick={onReview}>
          Review and save
        </Button>
      )}
    </div>
  );
}

export function WhatChanges({
  name,
  lines,
  note,
  sources,
  error,
  isSaving,
  onKeepEditing,
  onSave,
}: {
  readonly name: string;
  readonly lines: readonly DiffLine[];
  readonly note: string;
  readonly sources: number;
  readonly error: string | null;
  readonly isSaving: boolean;
  readonly onKeepEditing: () => void;
  readonly onSave: () => void;
}) {
  const count = `${String(lines.length)} ${lines.length === 1 ? 'value' : 'values'}`;
  const sourceText =
    sources === 0 ? 'no sources' : `${String(sources)} ${sources === 1 ? 'source' : 'sources'}`;
  return (
    <section
      id="what-changes"
      aria-labelledby="what-changes-title"
      className="overflow-hidden rounded-xl border border-input bg-card shadow-xs"
    >
      <header className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-[18px] pt-3.5 pb-3">
        <h3
          id="what-changes-title"
          tabIndex={-1}
          className="text-[15px] font-semibold text-foreground outline-none"
        >
          What changes
        </h3>
        <span className="text-[12.5px] text-muted-foreground">
          <Figures text={`Check before saving: ${count} on ${name}`} />
        </span>
      </header>
      {lines.map((line) => (
        <div
          key={`${line.label}:${line.before}`}
          className="grid grid-cols-[minmax(170px,0.7fr)_minmax(0,1.3fr)] items-baseline gap-3 border-t border-border px-[18px] py-2 text-[13px]"
        >
          <span className="text-muted-foreground">{line.label}</span>
          <BeforeAfter before={line.before} after={line.after} />
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-border bg-secondary/40 px-[18px] py-3.5">
        <span className="flex min-w-[20rem] flex-1 flex-col gap-1 text-[12.5px] text-muted-foreground">
          <span>
            Goes into History with your name, {note.trim() === '' ? 'no note' : 'your note'} and {sourceText}.
            Anyone can revert it there.
          </span>
          <span>Past logs keep the numbers they were saved with. New logs use these from now on.</span>
          {error === null ? null : (
            <span role="alert" className="font-medium text-err-fg">
              {error}
            </span>
          )}
        </span>
        <Button variant="ghost" size="lg" onClick={onKeepEditing} disabled={isSaving}>
          Keep editing
        </Button>
        <Button size="lg" onClick={onSave} disabled={isSaving}>
          {isSaving ? 'Saving…' : `Save ${count} to ${name}`}
        </Button>
      </div>
    </section>
  );
}
